// src/ConsumerApp/Screens/ProductsList/ProductsList.js

import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Navbar, CenteredContainer } from '../../DesignSystem';
import ProductCard from './components/ProductCard';
import BulkRfqCategoryBanner from './components/BulkRfqCategoryBanner';
import Bucket from '../Bucket/Bucket';
import ProductOptionsModal from '../ProductDetails/components/ProductOptionsModal';
import DesktopFiltersSidebar from './components/DesktopFiltersSidebar';
import TabletFiltersModal from './components/TabletFiltersModal';
import { guestBucketManager } from '../../utils/guestBucketManager';

// SVGs
import X from '../../../assets/icons/X.svg';
import Package from '../../../assets/icons/Package.svg';

// Shared API Hooks
import {
  useGetSellingProducts,
  useGetBucketSummary,
  useGetFiltersByCategory,
  useGetProductVariants,
} from '../../api/products/product.hooks';
import {
  useGetSavedList,
  useAddItemToSavedList,
  useRemoveItemFromSavedList,
} from '../../api/savedList/savedList.hooks';
import { useGetDefaultDeliveryAddress } from '../../api/projects/project.hooks';

export default function ProductsList() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 1. Category Resolution
  const categoryId = location.state?.categoryId || searchParams.get('categoryId');
  const categoryName = location.state?.categoryName || searchParams.get('categoryName') || 'Products';

  // 2. Reactive Customer Session
  const [customer, setCustomer] = useState(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const syncAuth = () => {
      try {
        const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
        setCustomer(stored ? JSON.parse(stored) : null);
      } catch {
        setCustomer(null);
      }
    };

    window.addEventListener('auth-state-change', syncAuth);
    window.addEventListener('storage', syncAuth);
    return () => {
      window.removeEventListener('auth-state-change', syncAuth);
      window.removeEventListener('storage', syncAuth);
    };
  }, []);

  const customerId = customer?._id || customer?.id;
  const hubId = customer?.hubId;

  // 3. Guest Bucket Listener
  const [guestBucketMap, setGuestBucketMap] = useState(() => guestBucketManager.getQuantityMap());

  useEffect(() => {
    const handleGuestChange = () => {
      setGuestBucketMap(guestBucketManager.getQuantityMap());
    };

    window.addEventListener('guest-bucket-change', handleGuestChange);
    return () => window.removeEventListener('guest-bucket-change', handleGuestChange);
  }, []);

  const [searchQuery, setSearchQuery] = useState('');
  const [isBucketOpen, setIsBucketOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [isTabletFilterModalOpen, setIsTabletFilterModalOpen] = useState(false);
  const [page, setPage] = useState(1);

  // Options Sheet State
  const [isOptionsSheetVisible, setIsOptionsSheetVisible] = useState(false);
  const [selectedProductWithOptions, setSelectedProductWithOptions] = useState([]);
  const [isOptionsAvailableForBuyNow, setIsOptionsAvailableForBuyNow] = useState(true);

  // 4. API Queries & Mutations
  const { data: rawAddress } = useGetDefaultDeliveryAddress(customerId);
  const { data: bucketSummary } = useGetBucketSummary(customerId);
  const { data: rawFiltersData } = useGetFiltersByCategory(categoryId);
  const { data: savedList = [] } = useGetSavedList(customerId);

  const addItemToSavedMutation = useAddItemToSavedList();
  const removeItemFromSavedMutation = useRemoveItemFromSavedList();
  const { mutateAsync: fetchProductVariants } = useGetProductVariants();

  const memoizedFilters = useMemo(() => {
    if (!rawFiltersData || !Array.isArray(rawFiltersData)) return [];

    const formattedCategories = [];

    rawFiltersData.forEach((subCategoryGroup) => {
      if (subCategoryGroup.filters && Array.isArray(subCategoryGroup.filters)) {
        subCategoryGroup.filters.forEach((filterAttr) => {
          if (filterAttr.filterable && filterAttr.attribute_values?.length > 0) {
            const formattedCategory = {
              id: filterAttr.attribute_name_id,
              label: filterAttr.attribute_name,
              options: filterAttr.attribute_values.map((val) => ({
                id: val.attribute_value_id,
                label: val.attribute_value,
              })),
            };

            const existingIndex = formattedCategories.findIndex((c) => c.id === formattedCategory.id);

            if (existingIndex === -1) {
              formattedCategories.push(formattedCategory);
            } else {
              formattedCategory.options.forEach((newOption) => {
                const exists = formattedCategories[existingIndex].options.some((o) => o.id === newOption.id);
                if (!exists) {
                  formattedCategories[existingIndex].options.push(newOption);
                }
              });
            }
          }
        });
      }
    });

    return formattedCategories;
  }, [rawFiltersData]);

  const attributeValueIds = useMemo(() => appliedFilters.map((f) => f.optionId), [appliedFilters]);

  const {
    data: rawProductData,
    isLoading: isProductsLoading,
    isError,
    error,
  } = useGetSellingProducts({
    customer_id: customerId,
    product_category_id: categoryId,
    attribute_value_ids: attributeValueIds,
    page: page,
    page_size: 40,
  });

  console.log(rawProductData);

  const savedItemsSet = useMemo(() => {
    const list = Array.isArray(savedList) ? savedList : savedList?.items || [];
    return new Set(list.map((item) => String(item.product_id || item.item_id || item._id)));
  }, [savedList]);

  const bucketItemsMap = useMemo(() => {
    const map = new Map();
    const buyNowItems = bucketSummary?.buy_now_bucket?.items || [];
    buyNowItems.forEach((bItem) => {
      if (bItem?.item_id) {
        map.set(String(bItem.item_id), {
          quantity: bItem.quantity,
          bucketId: bucketSummary?.buy_now_bucket?.bucket_id || bucketSummary?.buy_now_bucket?._id,
        });
      }
    });
    return map;
  }, [bucketSummary]);

  const getProductQuantity = (pid) => {
    if (customerId) {
      return bucketItemsMap.get(pid)?.quantity;
    }
    return guestBucketMap.get(pid);
  };

  const products = useMemo(() => {
    const list =
      rawProductData?.products ||
      rawProductData?.data?.products ||
      (Array.isArray(rawProductData) ? rawProductData : []);

    if (!searchQuery.trim()) return list;
    return list.filter((p) =>
      (p.product_name || p.name || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [rawProductData, searchQuery]);

  const isCementCategory = categoryName?.toLowerCase().includes('cement');

  const handleToggleFilter = (category, option) => {
    setAppliedFilters((prev) => {
      const exists = prev.find((f) => f.optionId === option.id);
      if (exists) {
        return prev.filter((f) => f.optionId !== option.id);
      }
      return [
        ...prev,
        {
          categoryId: category.id,
          categoryLabel: category.label,
          optionId: option.id,
          label: option.label,
        },
      ];
    });
  };

  const handleRemoveFilter = (id) => {
    setAppliedFilters((prev) => prev.filter((f) => f.optionId !== id));
  };

  const handleClearAllFilters = () => {
    setAppliedFilters([]);
  };

  const handleToggleSave = async (item, shouldSave) => {
    if (!customerId) {
      navigate('/consumer');
      return false;
    }
    const pid = String(item.product_id || item._id || item.id);
    try {
      if (shouldSave) {
        await addItemToSavedMutation.mutateAsync({ customer_id: customerId, product_id: pid });
      } else {
        await removeItemFromSavedMutation.mutateAsync({ customer_id: customerId, product_id: pid });
      }
      return true;
    } catch (err) {
      console.error('Failed to toggle saved item:', err);
      return false;
    }
  };

  const handleOpenOptions = async (productItem, isBuyNowAvailable) => {
    const pid = String(productItem.product_id || productItem._id);
    try {
      const purchase_mode = isBuyNowAvailable ? 'Buy Now' : 'Quote';
      const variantsResponse = await fetchProductVariants({
        product_id: pid,
        purchase_mode,
      });
      const list = Array.isArray(variantsResponse)
        ? variantsResponse
        : variantsResponse?.product_variants || [];

      setSelectedProductWithOptions(list);
      setIsOptionsAvailableForBuyNow(isBuyNowAvailable);
      setIsOptionsSheetVisible(true);
    } catch (error) {
      console.error('Error fetching product options:', error);
    }
  };

  return (
    <div style={styles.pageShell}>
      <style>{`
        /* ── Main Catalog Grid Container ── */
        .catalog-main-grid {
          display: grid;
          grid-template-columns: 260px 1fr;
          gap: 24px;
          width: 100%;
          align-items: start;
        }

        .tablet-filter-trigger-btn {
          display: none;
        }

        /* ── Desktop & Tablet Responsive Products Grid ── */
        .bb-products-responsive-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
          gap: 16px;
          width: 100%;
        }

        /* ── LAPTOP & TABLET (<= 1024px) ── */
        @media (max-width: 1024px) {
          .catalog-main-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }
          .desktop-filter-sidebar-col {
            display: none;
          }
          .tablet-filter-trigger-btn {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 8px 14px;
            background-color: #FFFFFF;
            border: 1px solid #CBD5E1;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 700;
            color: #2B2A29;
            cursor: pointer;
          }
        }

        /* ── MOBILE SCREENS: Exact 2-Column Zepto / Blinkit Grid (<= 640px) ── */
        @media (max-width: 640px) {
          .bb-products-responsive-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 10px !important;
          }
        }
      `}</style>

      <Navbar searchQuery={searchQuery} onSearchChange={setSearchQuery} />

      <main style={styles.mainFeed}>
        <CenteredContainer>
          <div style={styles.layout}>
            {/* Header Block */}
            <div style={styles.headerBlock}>
              <div>
                <h1 style={styles.categoryTitle}>{categoryName}</h1>
              </div>

              <div style={styles.headerRightActions}>
                <button
                  type="button"
                  className="tablet-filter-trigger-btn"
                  onClick={() => setIsTabletFilterModalOpen(true)}
                >
                  <span>Filters</span>
                  {appliedFilters.length > 0 && (
                    <span style={styles.tabletBtnBadge}>{appliedFilters.length}</span>
                  )}
                </button>

                <span style={styles.itemsCountBadge}>
                  {rawProductData?.pagination?.total_items || products.length} items
                </span>
              </div>
            </div>

            {/* Filter Tags */}
            {appliedFilters.length > 0 && (
              <div style={styles.tagsRow}>
                {appliedFilters.map((f) => (
                  <div key={f.optionId} style={styles.filterTag}>
                    <span>{f.label}</span>
                    <button
                      type="button"
                      style={styles.tagCloseBtn}
                      onClick={() => handleRemoveFilter(f.optionId)}
                    >
                      <img src={X} alt="remove" style={{ width: 9, height: 9 }} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  style={styles.clearAllBtn}
                  onClick={handleClearAllFilters}
                >
                  Clear all
                </button>
              </div>
            )}

            <div className="catalog-main-grid">
              {/* Desktop Filters Sidebar */}
              <div className="desktop-filter-sidebar-col">
                <DesktopFiltersSidebar
                  filterConfig={memoizedFilters}
                  appliedFilters={appliedFilters}
                  onToggleFilter={handleToggleFilter}
                  onClearAll={handleClearAllFilters}
                />
              </div>

              {/* Products Feed */}
              <div style={styles.catalogFlow}>
                {isProductsLoading ? (
                  <div style={styles.loadingBox}>Loading catalog items...</div>
                ) : isError ? (
                  <div style={styles.errorBox}>
                    Unable to load products. {error?.message || 'Please try again.'}
                  </div>
                ) : products.length === 0 ? (
                  <div style={styles.emptyBox}>
                    <img src={Package} alt="empty" style={{ width: 64, height: 64, opacity: 0.3 }} />
                    <h3 style={{ margin: '12px 0 4px 0', color: '#2B2A29' }}>No products found</h3>
                    <p style={{ margin: 0, color: '#707B8C', fontSize: 13 }}>
                      Try changing your search term or clearing applied filters.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* First 4 Products (2x2 on Mobile, 4x1 on Desktop) */}
                    <div className="bb-products-responsive-grid">
                      {products.slice(0, 4).map((p) => {
                        const pid = String(p.product_id || p._id);
                        return (
                          <ProductCard
                            key={pid}
                            item={p}
                            customerId={customerId}
                            hubId={hubId}
                            savedBucketQuantity={getProductQuantity(pid)}
                            bucketId={customerId ? bucketItemsMap.get(pid)?.bucketId : null}
                            isSavedItem={savedItemsSet.has(pid)}
                            onToggleSave={handleToggleSave}
                            onOptionsClick={handleOpenOptions}
                            onItemClick={() =>
                              navigate('/consumer/productdetails', { state: { productId: pid } })
                            }
                          />
                        );
                      })}
                    </div>

                    {/* Constrained & Centered RFQ Banner */}
                    {isCementCategory && <BulkRfqCategoryBanner categoryName={categoryName} />}

                    {/* Remaining Catalog Products */}
                    {products.length > 4 && (
                      <div className="bb-products-responsive-grid">
                        {products.slice(4).map((p) => {
                          const pid = String(p.product_id || p._id);
                          return (
                            <ProductCard
                              key={pid}
                              item={p}
                              customerId={customerId}
                              hubId={hubId}
                              savedBucketQuantity={getProductQuantity(pid)}
                              bucketId={customerId ? bucketItemsMap.get(pid)?.bucketId : null}
                              isSavedItem={savedItemsSet.has(pid)}
                              onToggleSave={handleToggleSave}
                              onOptionsClick={handleOpenOptions}
                              onItemClick={() =>
                                navigate('/consumer/productdetails', { state: { productId: pid } })
                              }
                            />
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </CenteredContainer>
      </main>

      <Bucket isOpen={isBucketOpen} onClose={() => setIsBucketOpen(false)} />

      <ProductOptionsModal
        visible={isOptionsSheetVisible}
        onClose={() => setIsOptionsSheetVisible(false)}
        cardTitle={isOptionsAvailableForBuyNow ? 'Add To Buy' : 'Add To Quote'}
        productData={selectedProductWithOptions}
        bucketItemsMap={bucketItemsMap}
        customerId={customerId}
        hubId={hubId}
        purchaseType={isOptionsAvailableForBuyNow ? 'BUY_NOW' : 'RFQ'}
      />

      <TabletFiltersModal
        visible={isTabletFilterModalOpen}
        onClose={() => setIsTabletFilterModalOpen(false)}
        filterConfig={memoizedFilters}
        appliedFilters={appliedFilters}
        onApply={(newFilters) => setAppliedFilters(newFilters)}
      />
    </div>
  );
}

const styles = {
  pageShell: {
    backgroundColor: '#FAF9F6',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  mainFeed: {
    width: '100%',
    padding: '20px 0 60px 0',
  },
  layout: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  headerBlock: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    borderBottom: '1px solid #E2E8F0',
    paddingBottom: 12,
  },
  categoryTitle: {
    fontSize: 22,
    fontWeight: '800',
    color: '#2B2A29',
    margin: 0,
  },
  headerRightActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  tabletBtnBadge: {
    backgroundColor: '#E9001E',
    color: '#FFFFFF',
    fontSize: 11,
    borderRadius: 999,
    padding: '1px 6px',
    fontWeight: '800',
  },
  itemsCountBadge: {
    fontSize: 13,
    color: '#707B8C',
    fontWeight: '600',
  },
  tagsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  filterTag: {
    backgroundColor: '#F5F3EF',
    border: '1px solid #CBD5E1',
    borderRadius: 20,
    padding: '4px 10px',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    color: '#2B2A29',
  },
  tagCloseBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    padding: 0,
  },
  clearAllBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#E9001E',
    fontSize: 12,
    fontWeight: '700',
  },
  catalogFlow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
  },
  loadingBox: {
    padding: 60,
    textAlign: 'center',
    color: '#707B8C',
    fontSize: 14,
  },
  errorBox: {
    padding: 60,
    textAlign: 'center',
    color: '#E9001E',
    fontSize: 14,
  },
  emptyBox: {
    padding: 60,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    border: '1px solid #E2E8F0',
  },
};